import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {useCreateCarisServiceRequestMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

const schema = z.object({description: z.string().trim().min(1)});

type FormType = z.infer<typeof schema>;

interface CreateCarisServiceRequestFormProps extends RequiredTestIdProps {
  vehicleId: string;
}

export function CreateCarisServiceRequestForm(props: CreateCarisServiceRequestFormProps) {
  const [createCarisServiceRequest] = useCreateCarisServiceRequestMutation();

  const onSubmit: FormSubmitHandler<FormType> = (values) =>
    createCarisServiceRequest({
      body: {vehicleId: props.vehicleId, description: values.description},
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));
      })
      .catch(handleApiError);

  return (
    <Form<FormType> experimentalZodSchema={schema} onSubmit={onSubmit}>
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            name="description"
            type="textarea"
            label={i18n.t('general.labels.description')}
            isRequired
            data-testid={suffixTestId('description', props)}
          />
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.discard')}
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('discard', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('entity.carisServiceRequest.actions.sendRequest')}
              data-testid={suffixTestId('send', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
